import PropTypes from 'prop-types'
import React, { Component } from 'react'
import Helmet from 'react-helmet'
import JsonLd from 'components/generic/JsonLd'
import { SITE_URL } from 'components/utils/structuredData'
import _ from 'lodash'
import classnames from 'classnames'
import { connect } from 'react-redux'
import LoadingComponent from 'components/generic/LoadingComponent'
import getLoadLimitByIndex from 'components/utils/getLoadLimitByIndex'
import { tutorialsStatus, tutorialsFetchMore } from 'reduxModules/tutorials'
import { tutorialsStateSelector as stateSelector } from 'reduxModules/tutorials/selectors'
import { tutorialsMergeProps as mergeProps } from 'reduxModules/tutorials/dispatchers'
import { TutorialSummaryPropTypes as tutorialProptypes } from 'components/generic/CourseSummary/propTypes'
import withRouterV6 from '../../../utils/withRouterV6'
import LoadMore from '../../generic/LoadMore'
import { categories as filterCategories, difficulties as filterDifficulties, ordering as orderingOptions } from './filters'
import TutorialListContent from './TutorialListContent'
import BannerSlideShow from '../../generic/BannerSlideShow'
import keyUpHandler from '../../utils/keyUpHandler'
import './styles.css'

export class TutorialList extends Component {
  constructor(props, context) {
    super(props, context)
    this.state = {
      search: ''
    }
    this.onChange = this.onChange.bind(this)
    this.inputKey = this.inputKey.bind(this)

    // debounce
    this.onChangeDebounce = _.debounce(this.inputKey, 350)
  }

  componentDidMount() {
    const {
      loadTutorials,
      loadWebinars,
      loadBanners,
      loadSpecialVideos,
      query,
      setFilters
    } = this.props

    // Use saved count from sessionStorage if returning to page, otherwise default
    const savedCount = sessionStorage.getItem('tutorialsLoadCount')
    const limit = savedCount ? parseInt(savedCount, 10) : 16

    if (!_.isEmpty(query)) {
      setFilters(query)
      loadTutorials({ limit, ...query })
      if (query.search) this.setState({ search: query.search })
    } else loadTutorials({ limit })

    loadWebinars()
    loadBanners()
    // TODO dont load special video if anon
    loadSpecialVideos()
  }

  componentDidUpdate(oldProps) {
    if (!_.isEqual(oldProps.filters, this.props.filters))
      this.props.loadTutorials(this.props.filters)

    // Restore scroll position when tutorials load
    const wasLoading = oldProps.requestStatus !== tutorialsStatus.success
    const isLoaded = this.props.requestStatus === tutorialsStatus.success
    const moreLoaded = this.props.tutorials?.length > oldProps.tutorials?.length

    if ((wasLoading && isLoaded) || moreLoaded) {
      const savedScrollY = sessionStorage.getItem('tutorialsScrollY')
      if (savedScrollY) {
        const targetY = parseInt(savedScrollY, 10)
        requestAnimationFrame(() => {
          const maxScroll = document.body.scrollHeight - window.innerHeight
          if (targetY <= maxScroll) {
            // We can scroll to target, do it and clear saved position
            sessionStorage.removeItem('tutorialsScrollY')
            sessionStorage.removeItem('tutorialsLoadCount')
            window.scrollTo(0, targetY)
          } else if (this.props.nextTutorials) {
            // Not enough content yet, scroll to bottom to trigger load more
            window.scrollTo(0, maxScroll)
          }
        })
      }
    }
  }

  componentWillUnmount() {
    // Save scroll position when navigating to tutorial detail
    const currentPath = window.location.pathname
    if (
      _.startsWith(currentPath, '/tutorials/') &&
      currentPath !== '/tutorials'
    ) {
      sessionStorage.setItem('tutorialsScrollY', window.scrollY.toString())
      sessionStorage.setItem('tutorialsLoadCount', this.props.tutorials?.length?.toString() || '16')
    }

    this.props.clearWebinarList()
    this.props.clearTutorialList()
    this.props.clearSpecialVideoList()
    this.props.clearFilters()
    this.onChangeDebounce.cancel()
  }

  onChange(event) {
    event.persist()
    this.setState({ search: event.target.value })
    this.onChangeDebounce(event)
  }

  inputKey(event) {
    const { location } = window
    const { value } = event.target
    const searchParams = new URLSearchParams(location.search)
    if (value) {
      searchParams.set('search', value)
      this.onSelectFilter('search', value)
    } else {
      this.onSelectFilter('search', '-1')
      searchParams.delete('search')
    }
    const newRelativePathQuery = `${
      location.pathname
    }?${searchParams.toString()}`
    this.props.router.push(newRelativePathQuery)
  }

  onSelectFilter(filter, value) {
    const { location } = window
    const searchParams = new URLSearchParams(location.search)
    let query = ''
    if (value === '-1' && filter in this.props.filters) {
      query = _.omit(this.props.filters, filter)
      this.props.setFilters(query)
      searchParams.delete(filter)
    } else {
      query = { ...this.props.filters, [filter]: value }

      if (!_.isEqual(query, this.props.query)) this.props.setFilters(query)

      searchParams.set(filter, value)
    }
    if ('URLSearchParams' in window) {
      const newRelativePathQuery = `${
        location.pathname
      }?${searchParams.toString()}`
      this.props.router.push(newRelativePathQuery)
    }
  }

  clearFilters() {
    const searchParams = new URLSearchParams(window.location?.search)
    searchParams.delete('difficulty')
    searchParams.delete('tags')
    this.props.router.push(`/tutorials/?${searchParams.toString()}`)
    this.props.clearFilters()
    if (this.props.isMobile && this.props.showFilters)
      this.props.toggleFilters()
  }

  clearSearch() {
    this.props.clearSearch()
    const searchParams = new URLSearchParams(window.location.search)
    searchParams.delete('search')
    this.props.router.push(`/tutorials/?${searchParams.toString()}`)

    this.setState({ search: '' })
  }

  render() {
    if (
      !this.props.tutorials ||
      (this.props.requestStatus !== tutorialsStatus.success &&
        this.props.requestStatus !== tutorialsStatus.filter)
    )
      return <LoadingComponent />

    const { props } = this

    return (
      <article id="TutorialList">
        <Helmet>
          <title>Online Courses | Stan Winston School of Character Arts</title>
          <meta
            name="description"
            content="Browse on-demand courses in special-effects makeup, creature design, sculpting, mold making, and animatronics — taught by the artists behind Hollywood's most iconic characters."
          />
        </Helmet>
        {props.tutorials && props.tutorials.length > 0 && (
          <JsonLd
            data={{
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: props.tutorials.map((tutorial, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                url: `${SITE_URL}/tutorials/${tutorial.slug}`,
                name: tutorial.title
              }))
            }}
          />
        )}
        {props.banners && (
          <BannerSlideShow
            banners={props.banners}
            isMobile={this.props.isMobile}
          />
        )}
        <section className="heading wrapper">
          <div className="search-box search-box-mobile">
            <form action="." onSubmit={(e) => e.preventDefault()}>
              <i className="icon-search" />
              <input
                aria-label="Search Courses"
                type="search"
                name="search-input"
                onChange={this.onChange}
                value={this.state.search}
                placeholder="Search Courses"
              />
            </form>
          </div>
          <h1>Courses</h1>
          <span className="filter-toggle" onClick={() => this.props.toggleFilters()}>
            Filters <i className={classnames('icon-right-2', { rotate: props.showFilters })} />
          </span>
          {!_.isEmpty(props.filters) && (
            <span className="mobile-clear-filters" onClick={() => this.clearFilters()}>
              Clear
              <i className="remove-filters icon-close" />
            </span>
          )}
          {!props.showFilters && (
            <div className="search-box search-box-bar">
              <i className="icon-search" />
              <form action="." onSubmit={(e) => e.preventDefault()}>
                <input
                  aria-label="Search Courses"
                  type="search"
                  name="search-input"
                  onChange={this.onChange}
                  value={this.state.search}
                  placeholder="Search Courses"
                />
              </form>
              {this.state.search.length > 0 && (
                <i
                  aria-label="Clear search on course list"
                  className="clear-search icon-close"
                  onClick={() => this.clearSearch()}
                  onKeyUp={event => keyUpHandler(event, this.clearSearch, null, this)}
                  tabIndex="0"
                />
              )}
            </div>
          )}
          <div className={classnames('filters', { expanded: props.showFilters })}>
            <select
              aria-label="Difficulty"
              className="difficulty-select"
              onChange={e => this.onSelectFilter('difficulty', e.target.value)}
              value={props.filters.difficulty ? props.filters.difficulty : '-1'}
            >
              <option value="-1">Difficulty</option>
              {filterDifficulties.map((difficulty, difficultyIndex) => (
                <option value={difficulty.value} key={difficultyIndex}>
                  {difficulty.name}
                </option>
              ))}
            </select>
            <select
              aria-label="Category"
              className="category-select"
              onChange={e => this.onSelectFilter('tags', e.target.value)}
              value={props.filters.tags ? props.filters.tags : '-1'}
            >
              <option value="-1">Category</option>
              {filterCategories.map((category, categoryIndex) => (
                <option value={category} key={categoryIndex}>{category}</option>
              ))}
            </select>
            <div className="filters-close-container">
              {(props.filters.tags || props.filters.difficulty) && !props.isMobile && (
                <span
                  aria-label="Remove filters on course list"
                  className="remove-filters"
                  onClick={() => this.clearFilters()}
                  onKeyUp={event => keyUpHandler(event, this.clearFilters, null, this)}
                  tabIndex="0"
                >
                  Clear filters
                </span>
              )}
            </div>
          </div>
        </section>

        <TutorialListContent
          specialVideos={this.props.specialVideos}
          webinars={this.props.webinars}
          tutorials={this.props.tutorials}
          requestStatus={this.props.requestStatus}
          filters={this.props.filters}
        />

        {this.props.tutorials.length === 0 && !_.isEmpty(this.props.filters) && this.props.requestStatus === tutorialsStatus.success && (
          <div className="no-results">No results</div>
        )}

        {this.props.nextTutorials && (
          <div className="load-more wrapper">
            <LoadMore
              fetchMoreAction={tutorialsFetchMore}
              next={this.props.nextTutorials}
              listContainerId="TutorialList"
            />
          </div>
        )}
      </article>
    )
  }
}

TutorialList.propTypes = {
  requestStatus: PropTypes.string.isRequired,
  showFilters: PropTypes.bool,
  loadTutorials: PropTypes.func.isRequired,
  loadWebinars: PropTypes.func.isRequired,
  loadBanners: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  clearFilters: PropTypes.func.isRequired,
  toggleFilters: PropTypes.func.isRequired,
  clearTutorialList: PropTypes.func.isRequired,
  clearWebinarList: PropTypes.func.isRequired,
  tutorials: PropTypes.arrayOf(PropTypes.shape(tutorialProptypes)),
  filters: PropTypes.shape({
    difficulty: PropTypes.string,
    category: PropTypes.string
  }),
  loadSpecialVideos: PropTypes.func.isRequired,
  clearSpecialVideoList: PropTypes.func.isRequired,
  router: PropTypes.object,
  query: PropTypes.object
}

TutorialList.defaultProps = {
  tutorials: [],
  filters: {},
  showFilters: false,
  router: {},
  query: {}
}

export default withRouterV6(connect(
  stateSelector,
  null,
  mergeProps
)(TutorialList))
